/**
 * PositionAssignmentSelector Component
 * Dropdown for assigning a coverage responsibility to a single defensive position.
 * Options come from the position assignment registry (src/rules/positionAssignments.ts),
 * so as new per-position behaviors are registered there, they appear here automatically.
 *
 * Keyboard behaviour lives in useListbox. Nothing here is paywalled — the whole page is already
 * behind the premium gate — so there is no locked-row treatment.
 */

import { CoverageType, DefensePositionId } from '../types';
import { getAssignmentOptions } from '../rules';
import { DEFENSE_POSITION_LABELS } from '../rules/positionAssignments';
import { useListbox } from '../hooks';
import './PositionAssignmentSelector.css';

interface PositionAssignmentSelectorProps {
  position: DefensePositionId;
  selectedCoverage: CoverageType;
  onCoverageChange: (coverage: CoverageType) => void;
  disabled?: boolean;
  /** Base id for the listbox and option ids. Distinct per instance. */
  id?: string;
}

function PositionAssignmentSelector({
  position,
  selectedCoverage,
  onCoverageChange,
  disabled = false,
  id = 'assignment-select',
}: PositionAssignmentSelectorProps) {
  const options = getAssignmentOptions(position);
  const selectedOption = options.find((option) => option.coverage === selectedCoverage);
  const selectedIndex = options.findIndex((option) => option.coverage === selectedCoverage);

  const handleActivate = (index: number) => {
    const option = options[index];
    if (!option) return;
    onCoverageChange(option.coverage);
    listbox.close();
  };

  const listbox = useListbox({
    itemCount: options.length,
    selectedIndex,
    onActivate: handleActivate,
    id,
    disabled,
  });

  return (
    <div className="assignment-selector" ref={listbox.containerRef}>
      <div
        className={`assignment-select-trigger ${disabled ? 'disabled' : ''}`}
        id={id}
        // The human label, not the raw id. This used to read out
        // "Select coverage assignment for DBLB1", leaking an internal identifier to a coach.
        aria-label={`Select coverage assignment for ${DEFENSE_POSITION_LABELS[position]}`}
        {...listbox.triggerProps}
      >
        <span>{selectedOption?.label ?? selectedCoverage}</span>
        <span className="assignment-select-arrow" aria-hidden="true">▼</span>
      </div>

      {listbox.isOpen && !disabled && (
        <ul className="assignment-select-dropdown" {...listbox.listProps}>
          {options.map((option, index) => {
            const isSelected = option.coverage === selectedCoverage;
            return (
              <li
                key={option.coverage}
                className={`assignment-option ${isSelected ? 'selected' : ''} ${!option.isImplemented ? 'placeholder' : ''}`}
                role="option"
                aria-selected={isSelected}
                {...listbox.getOptionProps(index)}
              >
                <span className="assignment-option-name">{option.label}</span>
                {!option.isImplemented && (
                  <span className="assignment-placeholder-badge">Holds Alignment</span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {selectedOption?.description && (
        <p className="assignment-description">{selectedOption.description}</p>
      )}
    </div>
  );
}

export default PositionAssignmentSelector;
